import DOMPurify from 'dompurify';
import { Box } from '@mui/material';
import { autoLinkHtml, TAB_SIZE } from 'utils';
import { LinkWithCopy } from 'components';
import parse, { domToReact } from 'html-react-parser';

type Props = { value: string };

const looksLikeHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);

const normalizeToHtml = (raw: string) => {
  const v = raw ?? '';
  if (!v.trim()) return '<p></p>';

  if (looksLikeHtml(v)) return v;

  const escaped = v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return `<p>${escaped.replace(/\r?\n/g, '<br />')}</p>`;
};

const preserveEmptyParagraphs = (html: string) => html.replace(/<p>\s*<\/p>/g, '<p><br /></p>');

const RichTextReadOnlyWithLinkCopy = ({ value }: Props) => {
  const normalized = normalizeToHtml(value || '');
  const fixed = preserveEmptyParagraphs(normalized);
  const linked = autoLinkHtml(fixed);
  const safeHtml = DOMPurify.sanitize(linked, {
    ALLOWED_TAGS: ['a', 'p', 'br', 'strong', 'em', 'u', 'span', 'div', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'start', 'type'],
  });

  return (
    <Box
      sx={{
        wordBreak: 'break-word',
        whiteSpace: 'break-spaces',
        tabSize: TAB_SIZE,
        '& a': {
          color: 'inherit',
          textDecorationColor: 'inherit',
        },
        '& p': { m: 0 },
        '& p:empty::after': { content: '"\\00a0"' },
        '& ul, & ol': {
          my: 0,
          pl: 3,
        },
        '& li': {
          my: 0.25,
        },
        '& ul': {
          listStyleType: 'disc',
          '& ul': {
            listStyleType: 'circle',
            '& ul': { listStyleType: 'square' },
          },
        },
        '& ol': {
          listStyleType: 'decimal',
          '& ol': {
            listStyleType: 'lower-alpha',
            '& ol': { listStyleType: 'lower-roman' },
          },
        },
      }}
      onClick={(e) => e.stopPropagation()}>
      {parse(safeHtml, {
        replace: (node: any) => {
          if (node?.type === 'tag' && node?.name === 'a') {
            const href = node.attribs?.href || '';
            if (!href) return;

            const children = domToReact(node.children || []);

            return <LinkWithCopy href={href}>{children}</LinkWithCopy>;
          }
          return undefined;
        },
      })}
    </Box>
  );
};

export default RichTextReadOnlyWithLinkCopy;
